import { useState, useCallback, useEffect, useRef } from 'react'
import {
  PartnerRecruitment,
  RelatedEnterprise,
} from '../clients/kyousou_api_client'

import {
  getKyousouApiClient,
  ApiErrorResponse,
  handleError,
} from '../services/network'
import { useSnackBar } from '../providers/snack_bar_provider'

/**
 * パートナー募集詳細情報を取得するフック
 * Kyousou APIから募集詳細と関連企業情報を取得
 */
export const useRecruitmentDetail = (recruitmentId: string | undefined) => {
  // ========================================
  // State管理
  // ========================================
  const [recruitment, setRecruitment] = useState<PartnerRecruitment | null>(
    null
  )
  const [relatedEnterprises, setRelatedEnterprises] = useState<
    RelatedEnterprise[]
  >([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apiClientRef = useRef(getKyousouApiClient())
  const { showSnackBar } = useSnackBar()

  // ========================================
  // データ取得処理
  // ========================================
  const fetchRecruitmentDetail = useCallback(
    async (id: string) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClientRef.current.partnerRecruitments
          .partnerRecruitmentsDetail(id)
          .catch((e: ApiErrorResponse) => {
            handleError(e, showSnackBar)
            return null
          })

        if (response) {
          setRecruitment(response.data)

          // 関連企業データを取得（企業IDを使用）
          if (response.data.enterprise?.id) {
            try {
              const relatedResponse =
                await apiClientRef.current.enterprise.relatedList(
                  response.data.enterprise.id.toString()
                )
              setRelatedEnterprises(relatedResponse.data.relatedEnterprises)
            } catch (relatedError) {
              // 関連企業APIでエラーが発生した場合は空配列に設定
              setRelatedEnterprises([])
              // 関連企業が取得できなくても、メインの募集詳細は表示する
            }
          } else {
            setRelatedEnterprises([])
          }
        } else {
          setError('パートナー募集詳細の取得に失敗しました')
          setRecruitment(null)
        }
      } catch (error) {
        console.error('パートナー募集詳細の取得に失敗しました:', error)
        setError('パートナー募集詳細の取得に失敗しました')
        setRecruitment(null)
      } finally {
        setLoading(false)
      }
    },
    [showSnackBar]
  )

  // ========================================
  // Effect Hook
  // ========================================
  useEffect(() => {
    if (recruitmentId) {
      fetchRecruitmentDetail(recruitmentId)
    } else {
      setRecruitment(null)
      setRelatedEnterprises([])
      setError(null)
    }
  }, [recruitmentId, fetchRecruitmentDetail])

  return {
    recruitment,
    relatedEnterprises,
    loading,
    error,
    refetch: () =>
      recruitmentId ? fetchRecruitmentDetail(recruitmentId) : undefined,
  }
}
